"use client";

import { FC, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ICasinoWinningBet, IWinningTicketResponse } from "@finbackoffice/clientbff-client";
import { ClientBFFContext, RuntimeConfigContext } from "@finbackoffice/site-core";
import { WalletsContext } from "contexts";
import classNames from "classnames";
import { CasinoGameTypeEnum, IWinnersWidgetPropsConfig } from "@finbackoffice/fe-core";
import { AssetsDirectory, TranslationScopes } from "@finbackoffice/enums";
import Img from "components/base/img/Img";
import Translate from "components/base/translate/Translate";
import { CurrencyFormatter } from "components/base/currency-formater/CurrencyFormater";
import { Svg } from "components/base/svg/Svg";
import { Carousel, CarouselSlide } from "components/base/carousel/Carousel";
import { getCasinoGameImgSrc } from "utils/getCasinoGameImgSrc";
import CasinoWidgetContainer from "../../base/casino-widget-container/CasinoWidgetContainer";
import styles from "./top-winners.module.sass";

type IWinningBetsState = {
    slotBets: ICasinoWinningBet[] | null;
    liveCasinoBets: ICasinoWinningBet[] | null;
    sportBets: IWinningTicketResponse[] | null;
    loading: boolean;
};

enum ViewType {
    Slot = "slot",
    LiveCasino = "livecasino",
    Sport = "sport",
}

type IProps = {
    config: IWinnersWidgetPropsConfig;
};

const TopWinners: FC<IProps> = ({ config }) => {
    const runtimeConfig = useContext(RuntimeConfigContext);

    const client = useContext(ClientBFFContext);
    const { currentCurrency } = useContext(WalletsContext);

    const [winningBetsState, setWinningBetsState] = useState<IWinningBetsState>({
        slotBets: [],
        liveCasinoBets: [],
        sportBets: [],
        loading: true,
    });
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number | null>(null);
    const [slidesOrder, setSlidesOrder] = useState<ViewType[]>([]);

    const carouselSettings = useMemo(() => ({ loop: true, duration: 60 }), []);
    const carouselAutoplaySettings = useMemo(() => ({ delay: 3000 }), []);

    useEffect(() => {
        const updateSlidesOrder = (newState: IWinningBetsState) => {
            const newSlidesOrder = [];

            if (newState.slotBets) {
                newSlidesOrder.push(ViewType.Slot);
            }
            if (newState.liveCasinoBets) {
                newSlidesOrder.push(ViewType.LiveCasino);
            }
            if (newState.sportBets) {
                newSlidesOrder.push(ViewType.Sport);
            }

            setSlidesOrder(newSlidesOrder);
        };

        const loadData = async () => {
            if (currentCurrency) {
                setWinningBetsState((state) => ({
                    ...state,
                    loading: true,
                }));

                const payload = config.casinoGameId ? { game_id: config.casinoGameId } : undefined;

                try {
                    const [casinoStatsResponse, sportStatsResponse] = await Promise.all([
                        config.slots || config.livecasino || config.casinoGameId
                            ? client.getCasinoWinningBets(payload)
                            : undefined,
                        config.sport ? client.getWinningTickets() : undefined,
                    ]);

                    const updatedState = {
                        slotBets:
                            config.slots && casinoStatsResponse
                                ? casinoStatsResponse.slots.slice(0, 5)
                                : null,
                        liveCasinoBets:
                            config.livecasino && casinoStatsResponse
                                ? casinoStatsResponse.liveCasino.slice(0, 5)
                                : null,
                        sportBets: (config.sport && sportStatsResponse) || null,
                        loading: false,
                    };

                    setWinningBetsState(updatedState);

                    updateSlidesOrder(updatedState);
                } catch (_error: unknown) {
                    setWinningBetsState((state) => ({
                        ...state,
                        loading: false,
                    }));
                }
            }
        };

        void loadData();
    }, [currentCurrency, config, client]);

    const renderSportStat = useCallback(
        (bet: IWinningTicketResponse, index: number) => (
            <div key={index} className={styles.topWinner}>
                <div className={styles.name}>
                    <Img
                        wrapperClassName={styles.topWinnerImg}
                        source={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/top-winner-${index + 1}.svg`}
                        alt="winner"
                        width={22}
                        height={26}
                        prefixAppName
                    />
                    <div>
                        <p>
                            <span>{bet.username}</span>
                        </p>
                        <span>{`${bet.eventLocalized?.home_team} vs ${bet.eventLocalized?.away_team}`}</span>
                    </div>
                </div>
                <div className={styles.amount}>
                    <span>
                        <CurrencyFormatter
                            currency={bet.currency ?? ""}
                            amount={bet.stake_amount}
                            withCode={false}
                        />
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${bet.currency?.toLowerCase()}.svg`}
                            className="svg-icon crypto-currency"
                        />
                    </span>
                </div>
                <div className={styles.payout}>
                    <span>
                        <CurrencyFormatter
                            currency={bet.currency ?? ""}
                            amount={bet.win_amount}
                            withCode={false}
                        />
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${bet.currency?.toLowerCase()}.svg`}
                            className="svg-icon crypto-currency"
                        />
                    </span>
                </div>
            </div>
        ),
        [],
    );

    const renderCasinoStat = useCallback(
        (bet: ICasinoWinningBet, index: number, viewType: ViewType) => (
            <div key={index} className={styles.topWinner}>
                <div className={styles.name}>
                    <Img
                        wrapperClassName={styles.topWinnerImgCasino}
                        source={getCasinoGameImgSrc(
                            runtimeConfig,
                            bet.game_id,
                            viewType === ViewType.Slot
                                ? CasinoGameTypeEnum.VIDEO_SLOTS
                                : CasinoGameTypeEnum.LIVE_CASINO,
                            [],
                        )}
                        alt="winner"
                        width="0"
                        height="0"
                        style={{ width: "100%", height: "auto" }}
                        fallbackSrc={bet.image_square || undefined}
                    />
                    <div>
                        <p>
                            <span>{bet.username}</span>
                        </p>
                        <span>{bet.game_name}</span>
                    </div>
                </div>
                <div className={styles.amount}>
                    <span>
                        <CurrencyFormatter
                            currency={bet.currency ?? ""}
                            amount={bet.stake_amount}
                            withCode={false}
                        />
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${bet.currency?.toLowerCase()}.svg`}
                            className="svg-icon crypto-currency"
                        />
                    </span>
                </div>
                <div className={styles.payout}>
                    <span>
                        <CurrencyFormatter
                            currency={bet.currency ?? ""}
                            amount={bet.win_amount}
                            withCode={false}
                        />
                        <Svg
                            src={`/${AssetsDirectory.Common}/${AssetsDirectory.Currencies}/${bet.currency?.toLowerCase()}.svg`}
                            className="svg-icon crypto-currency"
                        />
                    </span>
                </div>
            </div>
        ),
        [runtimeConfig],
    );

    const renderCasinoContent = useCallback(
        (data: ICasinoWinningBet[], viewType: ViewType) => {
            if (data.length) {
                return (
                    <div className={classNames(styles.topWinnersList)}>
                        {data.map((stat, index) => renderCasinoStat(stat, index, viewType))}
                    </div>
                );
            }
            return (
                <span className="no-game">
                    <Translate tid="no_data" />
                </span>
            );
        },
        [renderCasinoStat],
    );

    const renderSportContent = useCallback(
        (data: IWinningTicketResponse[]) => {
            if (data.length) {
                return (
                    <div className={classNames(styles.topWinnersList)}>
                        {data.map(renderSportStat)}
                    </div>
                );
            }
            return (
                <span className="no-game">
                    <Translate tid="no_data" />
                </span>
            );
        },
        [renderSportStat],
    );

    const renderHeader = useMemo(
        () => (
            <>
                <span className={styles.headerTitle}>
                    <span>
                        {!winningBetsState.loading && (
                            <strong>
                                {config.id ? (
                                    <Translate
                                        tid={`casino_${config.id}_title`}
                                        namespace={TranslationScopes.CasinoWidgets}
                                    />
                                ) : (
                                    !!slidesOrder.length &&
                                    currentSlideIndex !== null && (
                                        <Translate
                                            tid={`${slidesOrder[currentSlideIndex]}_winners_title`}
                                            namespace={TranslationScopes.Casino}
                                        />
                                    )
                                )}
                            </strong>
                        )}
                    </span>
                    <Svg
                        src={`/${AssetsDirectory.Common}/${AssetsDirectory.BaseIcons}/crown.svg`}
                        className={styles.crown}
                        prefixAppName
                    />
                </span>
                <div className={styles.subHeader}>
                    <span>
                        <Translate
                            tid="casino_user_game_column_title"
                            namespace={TranslationScopes.CasinoWidgets}
                        />
                    </span>
                    <span>
                        <Translate
                            tid="casino_bet_amount_column_title"
                            namespace={TranslationScopes.CasinoWidgets}
                        />
                    </span>
                    <span>
                        <Translate
                            tid="casino_payout_column_title"
                            namespace={TranslationScopes.CasinoWidgets}
                        />
                    </span>
                </div>
            </>
        ),
        [config.id, currentSlideIndex, slidesOrder, winningBetsState.loading],
    );

    const renderSlide = useCallback(
        (slide: ViewType) => {
            switch (slide) {
                case ViewType.Slot:
                    return (
                        !!winningBetsState.slotBets &&
                        renderCasinoContent(winningBetsState.slotBets, slide)
                    );
                case ViewType.LiveCasino:
                    return (
                        !!winningBetsState.liveCasinoBets &&
                        renderCasinoContent(winningBetsState.liveCasinoBets, slide)
                    );
                case ViewType.Sport:
                    return (
                        !!winningBetsState.sportBets &&
                        renderSportContent(winningBetsState.sportBets)
                    );
                default:
                    return null;
            }
        },
        [
            renderCasinoContent,
            renderSportContent,
            winningBetsState.liveCasinoBets,
            winningBetsState.slotBets,
            winningBetsState.sportBets,
        ],
    );

    return (
        <CasinoWidgetContainer
            loading={winningBetsState.loading}
            contentContainerClassName={styles.container}
            contentClassName={styles.content}
            renderHeader={renderHeader}>
            <Carousel
                onSelectCallback={(currentIndex) => setCurrentSlideIndex(currentIndex)}
                options={carouselSettings}
                autoplay={carouselAutoplaySettings}
                wrapperClassName={styles.topWinnersCarousel}
                fade
                classnames>
                {slidesOrder.map((slide, index) => (
                    <CarouselSlide key={index}>{renderSlide(slide)}</CarouselSlide>
                ))}
            </Carousel>
        </CasinoWidgetContainer>
    );
};

export default TopWinners;
